import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/UI/dialog';
import Button from '@/components/UI/Button';
import Badge from '@/components/UI/Badge';
import { ordersAPI, positionsAPI, strategiesAPI } from '@/services/api';
import { Spinner } from '@/components/motion';
import formatMoney from '@/lib/formatMoney';

/* ── Helpers (shared with DeleteStrategyModal) ── */

const ORDER_TYPE_BADGES = {
  limit: { variant: 'info', label: 'LIMIT' },
  market: { variant: 'primary', label: 'MARKET' },
  stop: { variant: 'warning', label: 'STOP' },
  stop_limit: { variant: 'warning', label: 'STOP LMT' },
};

const getOrderTypeBadge = (type) => {
  const cfg = ORDER_TYPE_BADGES[type] || { variant: 'neutral', label: type?.toUpperCase() };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
};

const MiniSpinner = () => (
  <div className="animate-spin rounded-full h-3.5 w-3.5 border-[1.5px] border-yellow-400 border-t-transparent flex-shrink-0" />
);

const formatPrice = (v) =>
  v != null ? formatMoney(v) : null;

const formatCurrency = (v) => formatMoney(v);

/* ── Component ── */

function StopStrategyModal({ strategy, open, onClose, onConfirm }) {
  const [phase, setPhase] = useState('review'); // review | executing | complete | timeout
  const [orders, setOrders] = useState([]);
  const [positions, setPositions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [liquidatePositions, setLiquidatePositions] = useState(true);
  const [progress, setProgress] = useState('');
  const pollRef = useRef(null);

  // Load orders + positions ONLY when the modal first opens.
  // Use strategy.id (stable string) instead of strategy object ref
  // to avoid re-triggering when parent refetches strategy data.
  const strategyId = strategy?.id;
  useEffect(() => {
    if (!open || !strategyId) return;

    // Only reset to review when the modal freshly opens, not on re-renders
    setPhase((prev) => prev === 'review' || prev === 'complete' || prev === 'timeout' ? 'review' : prev);
    setLoading(true);
    setProgress('');

    Promise.all([
      ordersAPI.getByStrategy(strategyId, 'open').catch(() => ({ data: [] })),
      positionsAPI.getByStrategy(strategyId).catch(() => ({ data: [] })),
    ]).then(([ordersRes, positionsRes]) => {
      setOrders(ordersRes.data || []);
      setPositions(positionsRes.data || []);
      setLoading(false);
    });
  }, [open, strategyId]); // strategyId is a stable string, not an object ref

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  const pollForCompletion = () => {
    return new Promise((resolve) => {
      const startTime = Date.now();
      const TIMEOUT = 30000;
      const INTERVAL = 2000;

      pollRef.current = setInterval(async () => {
        try {
          const res = await positionsAPI.getByStrategy(strategy.id);
          const remaining = res.data || [];
          if (remaining.length === 0) {
            clearInterval(pollRef.current);
            pollRef.current = null;
            resolve('complete');
            return;
          }
        } catch {
          // ignore polling errors
        }

        if (Date.now() - startTime >= TIMEOUT) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          resolve('timeout');
        }
      }, INTERVAL);
    });
  };

  const executeLiquidation = async () => {
    setProgress('Liquidating positions at market...');
    try {
      await strategiesAPI.liquidate(strategy.id);
    } catch {
      // liquidation attempt sent, poll for result
    }

    setProgress('Waiting for positions to close...');
    const result = await pollForCompletion();
    return result;
  };

  const handleStop = async () => {
    setPhase('executing');

    // Step 1: Cancel orders
    setProgress('Canceling open orders...');
    try {
      await strategiesAPI.stop(strategy.id);
    } catch {
      // continue even if stop partially fails
    }

    // Step 2: Liquidate if checked
    if (liquidatePositions && positions.length > 0) {
      const result = await executeLiquidation();
      if (result === 'timeout') {
        setPhase('timeout');
        return;
      }
    }

    setPhase('complete');
  };

  const handleRetry = async () => {
    setPhase('executing');
    const result = await executeLiquidation();
    if (result === 'timeout') {
      setPhase('timeout');
      return;
    }
    setPhase('complete');
  };

  const handleForceClose = async () => {
    setProgress('Marking positions closed in database...');
    try {
      await strategiesAPI.forceClosePositions(strategy.id);
    } catch {
      // best effort
    }
    setPhase('complete');
  };

  const handleClose = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    if (phase === 'complete' || phase === 'timeout') {
      onConfirm?.();
    } else {
      onClose();
    }
  };

  const handleCancelTimeout = () => {
    // Strategy is already stopped, just close modal
    onConfirm?.();
  };

  if (!strategy) return null;

  const isExecuting = phase === 'executing';

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !isExecuting && handleClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Stop Strategy: &ldquo;{strategy.name}&rdquo;</DialogTitle>
          <DialogDescription>
            {phase === 'review' && 'Review the impact before stopping this strategy.'}
            {phase === 'executing' && progress}
            {phase === 'complete' && 'Strategy stopped successfully.'}
            {phase === 'timeout' && 'Position liquidation timed out.'}
          </DialogDescription>
        </DialogHeader>

        {/* ── REVIEW PHASE ── */}
        {phase === 'review' && (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : (
              <div className="space-y-5">
                {/* Unfilled Orders */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                    Unfilled Orders ({orders.length})
                  </h4>
                  {orders.length > 0 ? (
                    <>
                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 text-[10px] text-muted-foreground/60 font-medium px-3 py-1.5 border-b border-white/5">
                          <div>Side / Type</div>
                          <div className="text-right">Shares</div>
                          <div className="text-right">Price</div>
                          <div className="text-right">Symbol</div>
                        </div>
                        <div className="max-h-44 overflow-y-auto divide-y divide-white/5">
                          {orders.map((o) => {
                            const qty = parseFloat(o.quantity);
                            const price = o.limit_price ?? o.stop_price ?? o.price ?? null;
                            return (
                              <div key={o.id} className="px-3 py-2">
                                <div className="grid grid-cols-[1fr_auto_auto_auto] gap-3 items-center">
                                  <div className="flex items-center gap-1.5">
                                    <span className={`text-xs font-bold ${o.side === 'buy' ? 'text-green-500' : 'text-red-500'}`}>
                                      {o.side?.toUpperCase()}
                                    </span>
                                    {getOrderTypeBadge(o.order_type || o.type)}
                                  </div>
                                  <div className="text-xs text-muted-foreground text-right">{qty}</div>
                                  <div className="text-xs text-muted-foreground text-right">
                                    {formatPrice(price) || 'MKT'}
                                  </div>
                                  <div className="text-xs font-mono font-semibold text-foreground text-right">
                                    {o.symbol}
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground/60 mt-1.5">
                        These orders will be automatically canceled.
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground/60">No unfilled orders.</p>
                  )}
                </div>

                {/* Open Positions */}
                <div>
                  <h4 className="text-sm font-semibold text-foreground flex items-center gap-2 mb-2">
                    Open Positions ({positions.length})
                  </h4>
                  {positions.length > 0 ? (
                    <>
                      <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                        <div className="max-h-44 overflow-y-auto divide-y divide-white/5">
                          {positions.map((p) => {
                            const qty = parseFloat(p.quantity);
                            const entry = parseFloat(p.entry_price);
                            const current = parseFloat(p.current_price || p.entry_price);
                            const pnl = (current - entry) * qty;
                            const pnlPct = entry > 0 ? ((current - entry) / entry) * 100 : 0;

                            return (
                              <div key={p.id} className="px-3 py-2 flex items-center justify-between">
                                <div>
                                  <span className="font-semibold text-foreground text-sm">{p.symbol}</span>
                                  <span className="text-muted-foreground text-sm ml-2">
                                    {qty} shares @ {formatCurrency(entry)}
                                  </span>
                                </div>
                                <span className={`text-sm font-medium ${pnl >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                                  {formatMoney(pnl, { sign: true })} ({pnlPct >= 0 ? '+' : ''}{pnlPct.toFixed(2)}%)
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      </div>

                      {/* Liquidate checkbox */}
                      <label className="flex items-center gap-2 mt-2.5 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={liquidatePositions}
                          onChange={(e) => setLiquidatePositions(e.target.checked)}
                          className="rounded border-white/20 bg-white/5 text-primary focus:ring-primary/30"
                        />
                        <span className="text-sm text-foreground">Liquidate positions at market</span>
                      </label>
                      {!liquidatePositions && (
                        <p className="text-xs text-amber-400/80 mt-1">
                          Positions will remain open after stopping. You'll need to close them manually.
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="text-sm text-muted-foreground/60">No open positions.</p>
                  )}
                </div>
              </div>
            )}

            <DialogFooter className="mt-4">
              <Button variant="ghost" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleStop}
                disabled={loading}
              >
                Stop Strategy
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── EXECUTING PHASE ── */}
        {phase === 'executing' && (
          <div className="space-y-5">
            {/* Orders — show canceling animation */}
            {orders.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">
                  Canceling Orders ({orders.length})
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="max-h-44 overflow-y-auto divide-y divide-white/5">
                    {orders.map((o) => {
                      const qty = parseFloat(o.quantity);
                      const price = o.limit_price ?? o.stop_price ?? o.price ?? null;
                      return (
                        <div key={o.id} className="px-3 py-2">
                          <div className="flex items-center gap-2.5">
                            <MiniSpinner />
                            <span className="text-xs text-yellow-400 animate-pulse">
                              Canceling {o.side?.toUpperCase()} {qty} {o.symbol}
                              {formatPrice(price) ? ` @ ${formatPrice(price)}` : ''}...
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Positions — show liquidating animation if applicable */}
            {liquidatePositions && positions.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-foreground mb-2">
                  Liquidating Positions ({positions.length})
                </h4>
                <div className="bg-white/5 border border-white/10 rounded-xl overflow-hidden">
                  <div className="max-h-44 overflow-y-auto divide-y divide-white/5">
                    {positions.map((p) => (
                      <div key={p.id} className="px-3 py-2">
                        <div className="flex items-center gap-2.5">
                          <MiniSpinner />
                          <span className="text-xs text-yellow-400 animate-pulse">
                            Liquidating {p.symbol} ({parseFloat(p.quantity)} shares)...
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-center justify-center py-4">
              <Spinner />
            </div>
          </div>
        )}

        {/* ── COMPLETE PHASE ── */}
        {phase === 'complete' && (
          <>
            <div className="text-center py-6">
              <div className="text-4xl mb-3">&#10003;</div>
              <p className="text-sm text-muted-foreground">
                Strategy stopped{liquidatePositions && positions.length > 0 ? ' and positions liquidated' : ''}.
              </p>
            </div>
            <DialogFooter>
              <Button onClick={handleClose}>
                Close
              </Button>
            </DialogFooter>
          </>
        )}

        {/* ── TIMEOUT PHASE ── */}
        {phase === 'timeout' && (
          <>
            <div className="text-center py-4">
              <div className="text-4xl mb-3">&#9888;</div>
              <p className="text-sm text-foreground font-medium mb-1">Liquidation timed out</p>
              <p className="text-xs text-muted-foreground">
                Some positions may not have been closed at the broker. Choose how to proceed:
              </p>
            </div>
            <DialogFooter className="flex-col sm:flex-row gap-2">
              <Button variant="ghost" onClick={handleCancelTimeout}>
                Cancel
              </Button>
              <Button variant="warning" onClick={handleForceClose}>
                Close Manually
              </Button>
              <Button variant="primary" onClick={handleRetry}>
                Retry
              </Button>
            </DialogFooter>
            <p className="text-[11px] text-amber-400/70 text-center mt-2">
              "Close Manually" marks positions as closed in the database. You must close them at the broker yourself.
            </p>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default StopStrategyModal;
